import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'
import { orderBy } from 'lodash'
import { insertMessagesInItems } from 'lib/messagesLib'

import useSnackbar from '../Snackbar/useSnackbar'

const CARS_TO_ASSIGN_ENDPOINT = 'cars-to-assign-all'
const CARS_TO_ASSIGN_BLIND_ENDPOINT = 'cars-to-assign-blind'

const useCarsToAssign = ({ isBlind = false } = {}) => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    cars: [],
    total: 0,
    error: null,
  })

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error,
      }))
    },
    [setState]
  )

  const setSuccessFetching = useCallback(
    (data) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        isUpdating: false,
        ...data,
        error: null,
      }))
    },
    [setState]
  )

  const fetchCarsToAssign = useCallback(
    async (sessionId) => {
      setStartFetching()
      try {
        const endpoint = isBlind
          ? CARS_TO_ASSIGN_BLIND_ENDPOINT
          : CARS_TO_ASSIGN_ENDPOINT
        const results = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/${endpoint}`
        )
        const cars = orderBy(results.data.body.items, ['title'], ['asc'])

        const messagesResult = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/messages`
        )

        const messages = messagesResult.data.body.messages

        insertMessagesInItems(messages, cars, { fallbackSessionId: sessionId })

        setSuccessFetching({
          cars,
          total: results.data.body.total,
        })
      } catch (e) {
        let message =
          e.response?.data?.message ||
          t('common.error.fetchData', {
            attribute: e.response?.status,
          })

        setStopFetching(e)

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      setStartFetching,
      setStopFetching,
      setSuccessFetching,
      showSnackbarMessage,
      t,
      isBlind,
    ]
  )

  const getCarsToAssign = useCallback(
    async (sessionId) => {
      const endpoint = isBlind
        ? CARS_TO_ASSIGN_BLIND_ENDPOINT
        : CARS_TO_ASSIGN_ENDPOINT
      const results = await axios.get(
        `${config.apiBaseUrl}sessions/${sessionId}/${endpoint}`
      )
      const cars = orderBy(results.data.body.items, ['title'], ['asc'])

      const messagesResult = await axios.get(
        `${config.apiBaseUrl}sessions/${sessionId}/messages`
      )

      insertMessagesInItems(messagesResult.data.body.messages, cars, {
        fallbackSessionId: sessionId,
      })

      return cars
    },
    [isBlind]
  )

  const refetchCarsToAssign = useCallback(
    async (sessionId) => {
      try {
        const endpoint = isBlind
          ? CARS_TO_ASSIGN_BLIND_ENDPOINT
          : CARS_TO_ASSIGN_ENDPOINT
        const results = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}/${endpoint}`
        )

        setSuccessFetching({
          cars: orderBy(results.data.body.items, ['title'], ['asc']),
          total: results.data.body.total,
        })
      } catch (e) {
        let message =
          e.response?.data?.message ||
          t('common.error.fetchData', {
            attribute: e.response?.status,
          })

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [setSuccessFetching, showSnackbarMessage, t, isBlind]
  )

  return {
    cars: state.cars,
    total: state.total,
    isFetching: state.isFetching,
    isUpdating: state.isUpdating,
    error: state.error,
    fetchCarsToAssign,
    refetchCarsToAssign,
    getCarsToAssign,
  }
}

export default useCarsToAssign
